import { useEffect, useRef } from 'react';

/**
 * Identity of the thing being edited (entity id, or a load generation counter).
 * `null`/`undefined` means "not loaded yet".
 */
export type SeedKey = string | number | null | undefined;

/**
 * Content token for an unkeyed seed. Object identity is worthless here: edit
 * pages rebuild `initialData` inline on every render, so identity changed on
 * every parent re-render and wiped whatever the user had typed.
 */
const contentToken = (data: unknown): unknown => {
  if (data === null || typeof data !== 'object') return data;
  try {
    return JSON.stringify(data);
  } catch {
    return data; // cyclic / unserializable: fall back to identity
  }
};

/**
 * Seeds form state once per SEED IDENTITY rather than once per object identity.
 *
 * Pass a `seedKey` (the record id, or a load generation counter) wherever one
 * exists: it is the strong guarantee, and a background refetch of the SAME
 * record then cannot reset an in-progress edit.
 *
 * Without a key the seed still only re-runs when the CONTENT changes, so a
 * re-render that rebuilds an equal object is inert. That keeps a call site that
 * forgets `seedKey` safe from the plain re-render bug, while a modal that swaps
 * one record for another still re-seeds.
 */
export function useSeedEffect<T>(
  data: T,
  seedKey: SeedKey,
  seed: (data: T) => void,
): void {
  const dataRef = useRef(data);
  dataRef.current = data;
  const seedRef = useRef(seed);
  seedRef.current = seed;

  const token: unknown = seedKey === undefined ? contentToken(data) : seedKey;

  useEffect(() => {
    seedRef.current(dataRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
}
